import React, { useContext } from 'react'
import Avatar from '../Avatar/Avatar'
import css from './UserListItem.module.css'
import ButtonToolbar from '../../buttons/ButtonToolbar/ButtonToolbar'
import { UserContext } from '../../../context/UserContext'
import ButtonIcon from '../../buttons/ButtonIcon/ButtonIcon'
import { RotateCcw, UserX } from 'react-feather'
import { CheckCircle, Eye, SignIn, Icon } from '@phosphor-icons/react'
import { GymMemberData } from '../../../types/types'
import { AppContext } from '../../../context/AppContext'
import { Time } from '../TimeAgo/TimeAgo'
import Badge from '../Badge/Badge'
import LoadingSkeleton from '../../utility/LoadingSceleton/LoadingSkeleton'
import { joinedAtMs, trackedAtMs } from '../../../utilities/memberDates'

type Props = {
  data: GymMemberData
  onUserAction?: (action: string, data: GymMemberData) => void
  index?: number
  /**
   * Last mobile app open, ms. Null = no recent opens,
   * undefined = engagement data not available
   */
  lastOpenedAt?: number | null
  /** Shows a skeleton instead of the activity line */
  activityIsLoading?: boolean
}

const UserListItem = ({
  data,
  onUserAction,
  index,
  lastOpenedAt,
  activityIsLoading,
}: Props) => {
  const { userID } = useContext(UserContext)
  const { currentSpace } = useContext(AppContext)

  const isMe = userID === data.uid
  const isOwner = currentSpace?.ownerID === data.uid

  const canEditPermissions =
    currentSpace?.ownerID === userID || currentSpace?.permissions.canEditMembers

  const canEditMembers =
    currentSpace?.ownerID === userID ||
    currentSpace?.permissions.canEditWorkouts

  return (
    <li className={css['li']}>
      <div className={css['left']}>
        {index && <div className={css['index']}>{index}</div>}
        <Avatar
          name={data.name}
          photoURL={data.photoURL}
          size={56}
          className={css['avatar']}
          zoomable
        />
        <div className={css['text-info']}>
          <MemberName data={data} />
          <div className={css['secondary']}>
            {activityIsLoading ? (
              <LoadingSkeleton
                type="block"
                style={{
                  width: '220px',
                  height: '12px',
                  borderRadius: '4px',
                  marginTop: '4px',
                }}
              />
            ) : (
              <MemberStats data={data} lastOpenedAt={lastOpenedAt} />
            )}
          </div>
        </div>
      </div>
      {!isMe &&
        !isOwner &&
        (data.hasAccess ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            {canEditPermissions && (
              <ButtonToolbar
                type="light"
                className={css['button']}
                onClick={() => onUserAction && onUserAction('edit', data)}
              >
                Role & Permissions
              </ButtonToolbar>
            )}
            {canEditMembers && (
              <ButtonIcon
                type="light"
                className={css['button']}
                border={true}
                icon={<UserX size={16} />}
                destructive={true}
                onClick={() => onUserAction && onUserAction('remove', data)}
              />
            )}
          </div>
        ) : (
          canEditMembers && (
            <ButtonToolbar
              type="light"
              className={css['button']}
              icon={<RotateCcw size={16} />}
              onClick={() => onUserAction && onUserAction('restore', data)}
            >
              Restore Member
            </ButtonToolbar>
          )
        ))}
    </li>
  )
}

/**
 * Name with a role badge (anyone but athletes) and "you" for the viewer.
 * Also used by Top members
 */
export const MemberName = ({ data }: { data: GymMemberData }) => {
  const { userID } = useContext(UserContext)
  return (
    <div className={css['name']}>
      <div className={css['text']}>{data.name}</div>{' '}
      {data.role !== 'athlete' && (
        <Badge style={{ float: 'left' }}>{data.role}</Badge>
      )}
      {userID === data.uid && (
        <Badge style={{ marginRight: '-2px', float: 'left' }}>you</Badge>
      )}{' '}
    </div>
  )
}

/**
 * The activity line's dates: viewed, tracked, joined. `lastOpenedAt` as in
 * Props; undefined leaves out viewed. Also used by Top members
 */
export const MemberStats = ({
  data,
  lastOpenedAt,
}: {
  data: GymMemberData
  lastOpenedAt?: number | null
}) => {
  const joinedAtSeconds = joinedAtMs(data) / 1000

  // WORKAROUND: views come from PostHog, which never receives events from
  // some phones (e.g. a DNS/VPN ad blocker blocking app.posthog.com), while
  // tracked workouts are written to Firestore by the server. Tracking implies
  // viewing, so with no recorded view we show the last tracked date instead.
  // Remove once the mobile app records lastViewedAt in Firestore itself.
  const viewedAt = lastOpenedAt || trackedAtMs(data) || null

  return (
    <>
      {lastOpenedAt !== undefined && (
        <Stat icon={Eye} label="Viewed workouts">
          {viewedAt ? (
            <Time seconds={viewedAt / 1000} />
          ) : (
            'Not in the past year'
          )}
        </Stat>
      )}
      <Stat icon={CheckCircle} label="Tracked a workout">
        {data.lastActivityRecord ? (
          <Time seconds={data.lastActivityRecord.createdDate.seconds} />
        ) : (
          'Never'
        )}
      </Stat>
      {joinedAtSeconds > 0 && (
        <Stat icon={SignIn} label="Joined">
          <Time seconds={joinedAtSeconds} />
        </Stat>
      )}
    </>
  )
}

/**
 * One date in the activity line: icon, then the date. The label is read by
 * screen readers and shown as a tooltip
 */
const Stat = ({
  icon: StatIcon,
  label,
  children,
}: {
  icon: Icon
  label: string
  children: React.ReactNode
}) => (
  <span className={css['stat']} title={label}>
    {/* Bold: the regular stroke gets too thin at 14px */}
    <StatIcon
      size={14}
      weight="bold"
      aria-hidden
      className={css['stat-icon']}
    />
    <span className={css['visually-hidden']}>{label} </span>
    {children}
  </span>
)

export default UserListItem
